'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  InlineDialog,
  InlineDialogHeader,
  InlineDialogBody,
  InlineDialogTitle,
} from './inline-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Users,
  DollarSign,
  ListMusic,
  PenLine,
  AlertCircle,
  Star,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import type { DemoNote, DemoNowPlaying } from '../demo-types'

// Note category badge
function NoteCategoryBadge({ category }: { category: DemoNote['category'] }) {
  const config: Record<DemoNote['category'], { icon: typeof PenLine; color: string; label: string }> = {
    general: { icon: PenLine, color: 'text-zinc-400 bg-zinc-500/20 border-zinc-500/30', label: 'Note' },
    highlight: { icon: Star, color: 'text-amber-400 bg-amber-500/20 border-amber-500/30', label: 'Highlight' },
    issue: { icon: AlertCircle, color: 'text-red-400 bg-red-500/20 border-red-500/30', label: 'Issue' },
    audience: { icon: Users, color: 'text-blue-400 bg-blue-500/20 border-blue-500/30', label: 'Audience' },
    setlist: { icon: ListMusic, color: 'text-violet-400 bg-violet-500/20 border-violet-500/30', label: 'Setlist' },
    tip_related: { icon: DollarSign, color: 'text-green-400 bg-green-500/20 border-green-500/30', label: 'Tips' },
  }

  const { icon: Icon, color, label } = config[category]

  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${color}`}>
      <Icon className="h-2.5 w-2.5 mr-1" />
      {label}
    </Badge>
  )
}

export interface NotesPanelProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  notes: DemoNote[]
  nowPlaying: DemoNowPlaying | null
  onCreateNote: (content: string, category: DemoNote['category'], linkToCurrentSong: boolean) => void
  onDeleteNote: (noteId: string) => void
}

export function NotesPanel({
  isOpen,
  onOpenChange,
  notes,
  nowPlaying,
  onCreateNote,
  onDeleteNote,
}: NotesPanelProps) {
  const t = useTranslations('demo')
  const [newNoteContent, setNewNoteContent] = useState('')
  const [newNoteCategory, setNewNoteCategory] = useState<DemoNote['category']>('general')
  const [linkNoteToSong, setLinkNoteToSong] = useState(true)

  const handleCreateNote = () => {
    if (!newNoteContent.trim()) return
    onCreateNote(newNoteContent.trim(), newNoteCategory, linkNoteToSong && !!nowPlaying)
    setNewNoteContent('')
    toast.success(t('noteSaved'))
  }

  return (
    <InlineDialog open={isOpen} onOpenChange={onOpenChange} className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
      <InlineDialogHeader onClose={() => onOpenChange(false)}>
        <InlineDialogTitle className="flex items-center gap-2">
          <PenLine className="h-5 w-5 text-cyan-400" />
          {t('eventNotes')}
        </InlineDialogTitle>
      </InlineDialogHeader>
      <InlineDialogBody className="flex-1 overflow-hidden flex flex-col">
        <div className="space-y-3 pb-3 border-b border-zinc-800">
          <Textarea
            placeholder={t('notePlaceholder')}
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-white resize-none min-h-[80px]"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={newNoteCategory} onValueChange={(v) => setNewNoteCategory(v as DemoNote['category'])}>
              <SelectTrigger className="w-auto min-w-[140px] bg-zinc-800 border-zinc-700 text-white h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="highlight">Highlight</SelectItem>
                <SelectItem value="issue">Issue</SelectItem>
                <SelectItem value="audience">Audience</SelectItem>
                <SelectItem value="setlist">Setlist</SelectItem>
                <SelectItem value="tip_related">Tip-related</SelectItem>
              </SelectContent>
            </Select>
            {nowPlaying && (
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={linkNoteToSong}
                  onChange={(e) => setLinkNoteToSong(e.target.checked)}
                  className="rounded border-zinc-600 bg-zinc-800"
                />
                Link to &quot;{nowPlaying.song.title}&quot;
              </label>
            )}
            <Button size="sm" onClick={handleCreateNote} disabled={!newNoteContent.trim()} className="ml-auto bg-cyan-600 hover:bg-cyan-700">
              {t('addNote')}
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {notes.length > 0 ? (
            <div className="space-y-2">
              {notes.map((note) => (
                <div key={note.id} className="p-3 rounded-lg border bg-zinc-800/50 border-zinc-700/50 group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <NoteCategoryBadge category={note.category} />
                        <span className="text-[10px] text-zinc-500">
                          {new Date(note.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-white whitespace-pre-wrap">{note.content}</p>
                      {note.songTitle && (
                        <p className="text-xs text-zinc-500 mt-1">🎵 {note.songTitle}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteNote(note.id)}
                      className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400">
              <PenLine className="h-10 w-10 mx-auto mb-2 text-zinc-600" />
              <p className="text-sm">{t('noNotesYet')}</p>
              <p className="text-xs text-zinc-500 mt-1">{t('startDocumenting')}</p>
            </div>
          )}
        </div>
      </InlineDialogBody>
    </InlineDialog>
  )
}
